import React from 'react';
import { Subscription } from '@/types/subscription';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Mail, Trash2, Building2, Calendar, CreditCard } from 'lucide-react';
import { formatDate, getStatusBadge, getSubscriptionTypeBadge } from '@/utils/subscriptionFormatters';

interface MobileSubscriptionCardProps {
  subscription: Subscription;
  cancellingId: string | null;
  onCancel: (subscription: Subscription) => void;
}

export function MobileSubscriptionCard({ 
  subscription, 
  cancellingId, 
  onCancel 
}: MobileSubscriptionCardProps) {
  return (
    <Card className="mb-4">
      <CardContent className="p-4 space-y-4">
        {/* Customer Info */}
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-lg">{subscription.customerName}</h3>
              {subscription.isBusiness && subscription.businessName && (
                <div className="text-sm text-blue-700 font-medium flex items-center gap-1 mt-1">
                  <Building2 className="h-3 w-3" />
                  {subscription.businessName}
                </div>
              )}
            </div>
            {getStatusBadge(subscription)}
          </div>
          
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Mail className="h-3 w-3" />
            {subscription.email}
          </div>
        </div>

        {/* Address */}
        <div className="flex items-start gap-2 text-sm">
          <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
          <div>
            <div>{subscription.address}</div>
            <div className="text-muted-foreground">{subscription.postcode}</div>
          </div>
        </div>

        {/* Subscription Details */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground mb-1">Bin Day</div>
            <div className="capitalize">
              {subscription.multipleDays ? (
                <span>Multiple Days</span>
              ) : (
                subscription.binDay
              )}
            </div>
            {subscription.multipleDays && (
              <Badge variant="secondary" className="mt-1 text-xs">Multiple Days</Badge>
            )}
          </div>
          
          <div>
            <div className="text-muted-foreground mb-1">Type</div>
            <div className="space-y-1">
              {getSubscriptionTypeBadge(subscription)}
              {subscription.isBusiness && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 block w-fit">
                  <Building2 className="h-3 w-3 mr-1" />
                  Business
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Start Date & Total */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-muted-foreground">Start Date</div>
              <div>{formatDate(subscription.startDate)}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-muted-foreground">Total Paid</div>
              <div className="font-semibold">£{(subscription.totalPaid || 0).toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        {subscription.active && subscription.status !== 'cancelled' && (
          <div className="pt-2 border-t">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onCancel(subscription)}
              disabled={cancellingId === subscription.id}
              className="w-full"
            >
              {cancellingId === subscription.id ? (
                "Cancelling..."
              ) : (
                <>
                  <Trash2 className="h-3 w-3 mr-2" />
                  Cancel Subscription
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}